import { useEffect, useRef, useState } from 'react';
import { Swiper as SwiperJS, Virtual } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useTranslation } from 'react-i18next';
import { IonIcon, useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';
import { chevronBackOutline, chevronForwardOutline } from 'ionicons/icons';
import { RouteComponentProps } from 'react-router-dom';
import classNames from 'classnames';

import DetailsLayout from 'src/components/layout/DetailsLayout';
import ProductDetails from 'src/components/menu/ProductDetails';
import DetailsFooter from 'src/components/menu/details/DetailsFooter';
import { useBreakpoint, useCategoryProductsGetter } from 'src/hooks';
import { useDispatch, useSelector } from 'src/store';
import { resetProductCustomizations, selectProduct } from 'src/slices/product';
import classes from './MenuDetails.module.scss';

interface MenuDetailsProps extends RouteComponentProps {}

SwiperJS.use([Virtual]);

const MenuDetails: React.FC<MenuDetailsProps> = ({ history, location, match }) => {
  const swiperRef = useRef(null);
  // Distinguishes a real user swipe from programmatic positioning (initial deeplink/cart-rec open).
  const userSwipedRef = useRef(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { selectedProductId, products } = useSelector((state) => state.product);
  const { isDesktopView } = useBreakpoint();
  const [scrollToTop, setScrollToTop] = useState<number>(0);
  const categoryProducts = useCategoryProductsGetter();
  const product = (products ?? []).find((p) => p.id === selectedProductId);
  // Cart recommendations and Facebook-ad deeplinks select a product without switching
  // category, so it may not be in categoryProducts. Fall back to showing just that product
  // so the correct one always opens; normal browsing keeps the full category list for swiping.
  // Safety net: if the selection was lost (e.g. a deeplink race resets selectedProductId), still
  // render the category — or all products — so the user never lands on a blank, force-close-only
  // screen. Showing the wrong product is recoverable; a white screen is not.
  const fallbackProducts = categoryProducts.length > 0 ? categoryProducts : products ?? [];
  const swiperProducts = product && !categoryProducts.some((p) => p.id === product.id) ? [product] : fallbackProducts;
  const productIndex = swiperProducts.findIndex((p) => p.id === selectedProductId);
  const routerProps = { history, location, match };

  const onDetailsHeightChange = () => {
    if (swiperRef) {
      swiperRef?.current?.swiper?.update();
    }
  };

  const onSlideChange = (swiper) => {
    setScrollToTop(Date.now());
    // Ignore programmatic/initial slide settling: it briefly lands on slide 0 and would otherwise
    // overwrite the deeplinked/selected product. Only real user swipes change the selection.
    if (!userSwipedRef.current) return;
    const slidedToProduct = swiperProducts.find((_, index) => index === swiper?.activeIndex);
    if (slidedToProduct) {
      dispatch(selectProduct(slidedToProduct.id));
    }
  };

  // Keep the swiper on the selected product as selection/products settle (cold-start deeplinks
  // resolve selectedProductId and the products list asynchronously, after first render).
  useEffect(() => {
    if (productIndex >= 0) {
      swiperRef?.current?.swiper?.slideTo(productIndex, 0);
    }
  }, [productIndex]);

  useIonViewWillEnter(() => {
    userSwipedRef.current = false;
    swiperRef?.current?.swiper?.slideTo(productIndex, 0);
    swiperRef?.current?.swiper?.update();
  }, [productIndex]);

  useIonViewWillEnter(() => {
    if (isDesktopView) {
      history.replace('/tabs/menu');
    }
  }, [isDesktopView]);

  useEffect(() => {
    if (isDesktopView && location?.pathname === '/tabs/menu/details') {
      history.replace('/tabs/menu');
    }
  }, [isDesktopView, location?.pathname]);

  useIonViewWillLeave(() => {
    dispatch(resetProductCustomizations());
  }, []);

  const layoutFooter = <>{product && <DetailsFooter product={product} />}</>;

  return (
    <DetailsLayout
      meta={{
        title: t('-SEO Menu details page'),
        description: t('-SEO Menu details page description'),
      }}
      footer={layoutFooter}
      sidePadding={false}
      {...routerProps}
      scrollToTop={scrollToTop}
    >
      <IonIcon icon={chevronBackOutline} color="medium" className={classNames(classes.arrowIcon, classes.backIcon)} />
      <IonIcon
        icon={chevronForwardOutline}
        color="medium"
        className={classNames(classes.arrowIcon, classes.forwardIcon)}
      />
      <Swiper
        slidesPerView={1}
        pagination={false}
        autoHeight={Boolean(true)}
        initialSlide={productIndex >= 0 ? productIndex : 0}
        onTouchStart={() => {
          userSwipedRef.current = true;
        }}
        onSlideChange={onSlideChange}
        // @ts-ignore
        ref={swiperRef}
        virtual
      >
        {swiperProducts.map((p, index) => (
          <SwiperSlide key={p.id} virtualIndex={index}>
            <ProductDetails product={p} onContentHeightChange={onDetailsHeightChange} />
          </SwiperSlide>
        ))}
      </Swiper>
    </DetailsLayout>
  );
};

export default MenuDetails;
